import type {
    ClientPortalThread as ExistingThread,
    ClientPortalUserMessage as ExistingUserMessage,
    CustomerTaskOverview as TaskOverview,
} from '@einwert/valuation-api-client'
import { getSelectedCustomer } from 'ClientPortal/src/context/SelectedCustomerContext'

import {
    applyPatch,
    isKeyOf,
    joinDefinedStrings,
    ReactQueryOptions,
    sendToSentry,
    useUploadMultiple,
    WithReactQueryOptions,
} from 'ComponentLibrary'
import { QueryClient, useIsMutating, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getValuationApiClient } from 'ClientPortal/src/api/valuationApiClient'
import { Operation } from 'fast-json-patch'
import type { RootUser as User } from '@einwert/valuation-api-client'
import { cloneDeep } from 'lodash'
import { UUID } from 'ComponentLibrary/src/common/uuid'
import { useGetTasks } from 'ClientPortal/src/api/Queries/tasks'
import { useIamContext } from 'ClientPortal/src/context/IAmContext'
import { SnapshotNotFoundError } from 'ClientPortal/src/api/errors'
import { CREATE_MESSAGE_KEY, THREAD_KEYS } from 'ClientPortal/src/api/Queries/threads-keys'
import { TASK_KEYS } from 'ClientPortal/src/api/Queries/tasks-keys'
import { useGlobalMutationErrorHandler } from 'ClientPortal/src/api/useGlobalMutationErrorHandler'

const PATCH_THREAD_KEY = 'PATCH_THREAD'

export function useGetThreads({ orderId, ...options }: WithReactQueryOptions<{ orderId?: string }> = {}) {
    const einwertApiClient = getValuationApiClient().communications
    const queryKey = orderId !== undefined ? THREAD_KEYS.order(orderId) : THREAD_KEYS.all

    const isSaving = useIsMutating({ mutationKey: [PATCH_THREAD_KEY] }) > 0
    const { data, isPending } = useQuery({
        queryFn: ({ signal }) =>
            einwertApiClient.getCustomerThreads({ orderId, customerId: getSelectedCustomer()?.uuid || undefined }, { signal }),
        // eslint-disable-next-line @tanstack/query/exhaustive-deps
        queryKey,
        ...options,
        enabled: options.enabled !== false && !isSaving,
    })

    return { threads: data || [], isThreadsPending: isPending }
}

export const useDeleteThread = () => {
    const einwertApiClient = getValuationApiClient().communications
    const handleError = useGlobalMutationErrorHandler()

    const getDeleteMutation = (threadId: string) => {
        return einwertApiClient.deleteCustomerThread({ threadId, customerId: getSelectedCustomer()?.uuid || undefined })
    }

    const { mutate: deleteThread, isPending: isDeleteThreadPending } = useMutation({
        mutationFn: getDeleteMutation,
        onError: handleError,
        meta: {
            invalidates: [THREAD_KEYS.all],
        },
    })
    return { deleteThread, isDeleteThreadPending }
}

export function useCreateThread({ onSuccess, onError }: ReactQueryOptions<ExistingThread> = {}) {
    const client = getValuationApiClient().communications
    const handleError = useGlobalMutationErrorHandler(onError)

    const { isPending, isError, error, mutate } = useMutation({
        mutationFn: (thread: Omit<Parameters<typeof client.createCustomerThread>[0], 'customerId'>) =>
            client.createCustomerThread({ ...thread, customerId: getSelectedCustomer()?.uuid || undefined }),
        mutationKey: ['CreateThread'],
        onSuccess,
        onError: handleError,
        meta: {
            invalidates: [THREAD_KEYS.all],
        },
    })

    return {
        isCreatingThread: isPending,
        isCreateThreadError: isError,
        createThreadError: error,
        createThreadMutate: mutate,
    }
}

type ThreadsSnapshot = {
    [key: string]: ExistingThread[] | undefined
}

export function usePatchThread({ orderId, threadId, onSuccess, onError }: WithReactQueryOptions<{ orderId: string; threadId: string }>) {
    const einwertApiClient = getValuationApiClient().communications
    const queryClient = useQueryClient()
    const handleError = useGlobalMutationErrorHandler(onError)

    const patchThreadQuery = ({ operations }: { operations: Operation[] }) =>
        einwertApiClient.patchCustomerThread({ threadId, operations, customerId: getSelectedCustomer()?.uuid || undefined })
    const queryKeysForOptimisticUpdate = [THREAD_KEYS.all, THREAD_KEYS.order(orderId)]

    const mutationKey = [PATCH_THREAD_KEY, threadId]
    const { mutate, isPending } = useMutation({
        mutationKey,
        scope: { id: `${PATCH_THREAD_KEY}-${threadId}` },
        mutationFn: patchThreadQuery,
        onSuccess,
        onMutate: async ({ operations }) => {
            await queryClient.cancelQueries({ queryKey: THREAD_KEYS.all }) // cancel all queries related to threads

            const snapshots = createThreadsSnapshots(queryKeysForOptimisticUpdate, queryClient)

            updateCaches(queryKeysForOptimisticUpdate, () => threadUpdaterFactory(threadId, { operations }), queryClient)

            // Return a snapshot containing the data for each of the keys
            return { snapshots }
        },
        onError: (err, _, context) => {
            if (queryClient.isMutating({ mutationKey }) === 1) {
                queryKeysForOptimisticUpdate.forEach((queryKey) => {
                    const previousThread = context?.snapshots[queryKey.join()]?.find((thread) => thread.uuid === threadId)
                    if (previousThread) {
                        queryClient.setQueryData<ExistingThread[]>(queryKey, (threads) =>
                            threads?.map((thread) => (thread.uuid === threadId ? previousThread : thread))
                        )
                    }
                })
            }
            handleError(err)
        },
        onSettled: () => {
            // Earlier responses must not replace newer optimistic changes while saves are queued.
            if (queryClient.isMutating({ mutationKey: [PATCH_THREAD_KEY] }) === 1) {
                return queryClient.invalidateQueries({ queryKey: THREAD_KEYS.all })
            }
        },
    })

    return { patchThread: mutate, isPatchingThread: isPending }
}

function threadUpdaterFactory(threadId: UUID, { operations }: { operations: Operation[] }) {
    return function updater(previousThreads: ExistingThread[] | undefined) {
        if (!previousThreads) {
            return undefined
        }
        const updatedThread = previousThreads.find((thread) => thread.uuid === threadId)

        if (!updatedThread) {
            return undefined
        }

        const newThreads = cloneDeep(previousThreads)
        const updatedThreadIndex = newThreads.findIndex((thread) => thread.uuid === updatedThread.uuid)

        newThreads[updatedThreadIndex] = applyPatch(updatedThread, operations).newDocument
        // participant_users are readonly. We can easily handle the removal of a person, but we cannot handle the addition of a person optimistically
        newThreads[updatedThreadIndex].participant_users = newThreads[updatedThreadIndex].participant_users.filter((user) =>
            newThreads[updatedThreadIndex].participant_ids.includes(user.id)
        )

        return newThreads
    }
}

export function useCreateMessage({ orderId, threadId, onSuccess, onError }: WithReactQueryOptions<{ orderId: string; threadId: string }>) {
    const einwertApiClient = getValuationApiClient().communications
    const queryClient = useQueryClient()
    const handleError = useGlobalMutationErrorHandler(onError)

    const createMessageQuery = ({ content, mentions }: { content: string; mentions: number[]; user: User }) =>
        einwertApiClient.createCustomerMessage({ threadId, content, mentions, customerId: getSelectedCustomer()?.uuid || undefined })

    const queryKeysForOptimisticUpdate = [THREAD_KEYS.order(orderId), THREAD_KEYS.all]
    const { mutate, isPending } = useMutation({
        mutationKey: [CREATE_MESSAGE_KEY],
        mutationFn: createMessageQuery,
        onSuccess,
        onMutate: async (newMessage) => {
            await queryClient.cancelQueries({ queryKey: THREAD_KEYS.all })
            const snapshots = createThreadsSnapshots(queryKeysForOptimisticUpdate, queryClient)
            updateCaches(queryKeysForOptimisticUpdate, () => newMessageUpdaterFactory(threadId, { newMessage }), queryClient)

            // Return a snapshot containing the data for each of the keys
            return { snapshots }
        },
        onError: (err, _, context) => {
            revertOptimisticUpdates(context, queryKeysForOptimisticUpdate, queryClient)
            handleError(err)
        },
        onSettled: () => {
            void queryClient.invalidateQueries({ queryKey: THREAD_KEYS.all })
        },
    })

    return { createMessage: mutate, isPending }
}

function newMessageUpdaterFactory(threadId: UUID, { newMessage }: { newMessage: { content: string; user: User } }) {
    return function updater(previousThreads: ExistingThread[] | undefined) {
        if (!previousThreads) {
            return undefined
        }
        const updatedThread = previousThreads.find((thread) => thread.uuid === threadId)

        if (!updatedThread) {
            return undefined
        }
        const newlyReturnedMessage: ExistingUserMessage = {
            content: newMessage.content,
            organization_name: newMessage.user.organization_name,
            message_type: 'existing_message',
            author_type: 'internal_user',
            uuid: '',
            user_id: newMessage.user.id,
            user_name: joinDefinedStrings([newMessage.user.first_name, newMessage.user.last_name]),
            is_edited: false,
            images: [],
            documents: [],
            created_on: new Date().toISOString(),
            updated_on: new Date().toISOString(),
        }
        const updatedThreadIndex = previousThreads.indexOf(updatedThread)
        const newThreads = cloneDeep(previousThreads)
        newThreads[updatedThreadIndex].messages.push(newlyReturnedMessage)
        return newThreads
    }
}

export function useDeleteMessage({
    orderId,
    threadId,
    deletedMessageContent,
    onSuccess,
    onError,
}: WithReactQueryOptions<{ orderId: string; threadId: string; deletedMessageContent: string }>) {
    const einwertApiClient = getValuationApiClient().communications
    const queryClient = useQueryClient()
    const handleError = useGlobalMutationErrorHandler(onError)

    const deleteMessageQuery = ({ messageId }: { messageId: string }) =>
        einwertApiClient.deleteCustomerMessage({ threadId, messageId, customerId: getSelectedCustomer()?.uuid || undefined })
    const queryKeysForOptimisticUpdate = [THREAD_KEYS.order(orderId), THREAD_KEYS.all]

    const { mutate } = useMutation({
        mutationFn: deleteMessageQuery,
        onSuccess,
        onMutate: async ({ messageId }) => {
            await queryClient.cancelQueries({ queryKey: THREAD_KEYS.all })
            const snapshots = createThreadsSnapshots(queryKeysForOptimisticUpdate, queryClient)
            updateCaches(
                queryKeysForOptimisticUpdate,
                () => deleteMessageUpdaterFactory(threadId, { messageId, deletedMessageContent }),
                queryClient
            )

            // Return a snapshot containing the data for each of the keys
            return { snapshots }
        },
        onError: (err, _, context) => {
            revertOptimisticUpdates(context, queryKeysForOptimisticUpdate, queryClient)
            handleError(err)
        },
        onSettled: () => {
            void queryClient.invalidateQueries({ queryKey: THREAD_KEYS.all })
        },
    })

    return { removeMessage: mutate }
}

function deleteMessageUpdaterFactory(
    threadId: UUID,
    { messageId, deletedMessageContent }: { messageId: UUID; deletedMessageContent: string }
) {
    return function updater(previousThreads: ExistingThread[] | undefined) {
        if (!previousThreads) {
            return undefined
        }

        return previousThreads.map((thread) => {
            if (thread.uuid === threadId) {
                return {
                    ...thread,
                    messages: thread.messages.map((message) => {
                        if (message.uuid === messageId) {
                            return {
                                ...message,
                                content: deletedMessageContent,
                                message_type: 'deleted_message',
                            }
                        }
                        return message
                    }),
                }
            }
            return thread
        })
    }
}

export function usePatchMessage({ orderId, threadId, onError }: WithReactQueryOptions<{ orderId: string; threadId: string }>) {
    const einwertApiClient = getValuationApiClient().communications
    const queryClient = useQueryClient()
    const handleError = useGlobalMutationErrorHandler(onError)

    const patchMessageQuery = ({ messageId, operations }: { messageId: string; operations: Operation[] }) =>
        einwertApiClient.patchCustomerMessage({ threadId, messageId, operations, customerId: getSelectedCustomer()?.uuid || undefined })

    const queryKeysForOptimisticUpdate = [THREAD_KEYS.order(orderId), THREAD_KEYS.all]

    const { mutate, isPending } = useMutation({
        mutationFn: patchMessageQuery,
        onMutate: async ({ messageId, operations }) => {
            await queryClient.cancelQueries({ queryKey: THREAD_KEYS.all })
            const snapshots = createThreadsSnapshots(queryKeysForOptimisticUpdate, queryClient)
            updateCaches(queryKeysForOptimisticUpdate, () => patchMessageUpdaterFactory(threadId, { messageId, operations }), queryClient)

            // Return a snapshot containing the data for each of the keys
            return { snapshots }
        },
        onError: (err, _, context) => {
            revertOptimisticUpdates(context, queryKeysForOptimisticUpdate, queryClient)
            handleError(err)
        },
        onSettled: () => {
            void queryClient.invalidateQueries({ queryKey: THREAD_KEYS.all })
        },
    })

    return { patchMessage: mutate, isPatchingMessage: isPending }
}

function patchMessageUpdaterFactory(threadId: UUID, { messageId, operations }: { messageId: UUID; operations: Operation[] }) {
    return function updater(previousThreads: ExistingThread[] | undefined) {
        if (!previousThreads) {
            return undefined
        }
        const updatedThread = previousThreads.find((thread) => thread.uuid === threadId)

        if (!updatedThread) {
            return undefined
        }
        const updatedMessage = updatedThread.messages.find((message) => message.uuid === messageId)

        if (!updatedMessage) {
            return undefined
        }

        const updatedThreadIndex = previousThreads.indexOf(updatedThread)
        const updatedMessageIndex = updatedThread.messages.indexOf(updatedMessage)

        const newlyReturnedMessage = applyPatch(updatedMessage, operations).newDocument

        previousThreads[updatedThreadIndex].messages[updatedMessageIndex] = newlyReturnedMessage
        return previousThreads
    }
}

export function useUploadMessageImages({
    onSuccess,
    onError,
    orderId,
    threadId,
}: WithReactQueryOptions<{ orderId: string; threadId: string }>) {
    const einwertApiClient = getValuationApiClient().communications
    const queryClient = useQueryClient()
    const handleError = useGlobalMutationErrorHandler(onError)

    const mutation = useUploadMultiple<{ messageId: string; image: File }>({
        onSuccess: async (data) => {
            onSuccess?.(data)
            await queryClient.invalidateQueries({
                queryKey: THREAD_KEYS.order(orderId),
            })
            await queryClient.invalidateQueries({ queryKey: TASK_KEYS.all })
        },
        onError: handleError,
        mutationFn: ({ messageId, image }) =>
            einwertApiClient.uploadCustomerMessageImage({
                threadId,
                messageId,
                image,
                customerId: getSelectedCustomer()?.uuid || undefined,
            }),
        errorMsgFn: ({ image }) => image.name,
    })

    return {
        uploadMessageImages: mutation.uploadMultiple,
        isUploadingMessageImages: mutation.isUploading,
        uploadMessageImagesError: mutation.uploadError,
        failedImageUploads: mutation.failedUploads,
        numImagesToUpload: mutation.numToUpload,
    }
}

export function useUploadMessageDocuments({ onSuccess, onError, threadId }: WithReactQueryOptions<{ threadId: string }>) {
    const einwertApiClient = getValuationApiClient().communications
    const queryClient = useQueryClient()
    const handleError = useGlobalMutationErrorHandler(onError)

    const mutation = useUploadMultiple<{ messageId: string; document: File }>({
        onSuccess: async (data) => {
            onSuccess?.(data)
            await queryClient.invalidateQueries({ queryKey: THREAD_KEYS.all })
            await queryClient.invalidateQueries({ queryKey: TASK_KEYS.all })
        },
        onError: handleError,
        mutationFn: ({ messageId, document }) =>
            einwertApiClient.uploadCustomerMessageDocument({
                messageId,
                threadId,
                document,
                customerId: getSelectedCustomer()?.uuid || undefined,
            }),
        errorMsgFn: ({ document }) => document.name,
    })

    return {
        uploadMessageDocuments: mutation.uploadMultiple,
        isUploadingMessageDocuments: mutation.isUploading,
        uploadMessageDocumentsError: mutation.uploadError,
        failedDocumentUploads: mutation.failedUploads,
        numDocumentsToUpload: mutation.numToUpload,
    }
}

export const useFetchNewMessages = () => {
    const einwertApiClient = getValuationApiClient().communications
    const handleError = useGlobalMutationErrorHandler()

    const fetchNewMessageDocuments = einwertApiClient.getCustomerThread

    const { mutateAsync, isPending } = useMutation({
        mutationFn: (threadId: string) => fetchNewMessageDocuments({ threadId, customerId: getSelectedCustomer()?.uuid || undefined }),
        onError: handleError,
    })
    return { fetchNewMessages: mutateAsync, isFetchingNewMessages: isPending }
}

export type ThreadWithMetadata = ExistingThread & { associatedTask?: TaskOverview } & { isPinned?: boolean }

export function useGetSortedAndFilteredThreadsWithMetadata({ orderId }: { orderId?: UUID }) {
    const { threads, isThreadsPending } = useGetThreads({ orderId })

    const { tasks: tasksInOpen, isTasksPending: hasOpenTasksPending } = useGetTasks({
        orderId,
        status: 'open',
    })
    const { tasks: tasksInDone, isTasksPending: hasDoneTasksPending } = useGetTasks({
        orderId,
        status: 'done',
    })

    const allTasks = [...tasksInOpen, ...tasksInDone]

    const { user, isUserPending } = useIamContext()

    const pinnedThreads = user?.pinned_threads ?? []

    const threadsWithMetadata: ThreadWithMetadata[] = threads.map((thread: ThreadWithMetadata) => {
        const associatedTask = allTasks.find((task) => task.communication_thread === thread.uuid)
        const isPinned = pinnedThreads.includes(thread.uuid)
        thread.associatedTask = associatedTask
        thread.isPinned = isPinned
        return thread
    })

    const filteredAndSortedThreads = threadsWithMetadata
        ?.filter((thread) => !isThreadWithTaskAndNoMessages(thread))
        .sort((a, b) => {
            if (a.isPinned && !b.isPinned) {
                return -1
            }
            if (!a.isPinned && b.isPinned) {
                return 1
            }
            return b.updated_on.localeCompare(a.updated_on) // sort so thread most recently updated is at the top
        })
    const isTasksPending = hasOpenTasksPending || hasDoneTasksPending
    return {
        filteredAndSortedThreads,
        isThreadsPending: isThreadsPending || isUserPending || isTasksPending,
        isTasksPending,
        pinnedThreads,
        tasksInDone,
        tasksInOpen,
    }
}

export function isThreadWithTaskAndNoMessages(thread: ThreadWithMetadata) {
    return thread.messages.length === 0 && thread.associatedTask
}

function createThreadsSnapshots(queryKeys: string[][], queryClient: QueryClient) {
    return queryKeys.reduce<ThreadsSnapshot>(
        (acc, key) => ({
            ...acc,
            [key.join()]: queryClient.getQueryData<ExistingThread[]>(key),
        }),
        {} as ThreadsSnapshot
    )
}

function updateCaches(queryKeys: string[][], updater: () => unknown, queryClient: QueryClient) {
    queryKeys.forEach((key) => {
        try {
            queryClient.setQueryData(key, updater())
        } catch (error) {
            sendToSentry(error)
        }
    })
}

function revertOptimisticUpdates(context: { snapshots: object } | undefined, queryKeys: string[][], queryClient: QueryClient) {
    if (context?.snapshots) {
        queryKeys.forEach((key) => {
            const snapshotKey = key.join()
            if (!isKeyOf(snapshotKey, context.snapshots)) {
                throw new SnapshotNotFoundError(snapshotKey)
            }
            queryClient.setQueryData(key, context.snapshots[snapshotKey])
        })
    }
}
